// This file contains the helper functions for the cards in the dashboard

import { useState } from "react"
import down_arrow from "@/assets/down_arrow.svg"

type CardBaseProps = {
    children: React.ReactNode,
    isNew: boolean,
    hoverChildren?: React.ReactNode,
    canOpen?: boolean,
    className?: string,
    maxHeight?: number,
    hoverText?: string,
    hoverInsteadOfClick?: boolean
}

/**
 * The base card template that all other cards will use
 * 
 * @param children - The children to be rendered inside the card
 * @param isNew - Whether the card is new
 * @param hoverChildren - The buttons to be rendered when the card is hovered over (You'd need to pass the routes on here)
 * @param canOpen - Whether the card can be opened (default is true)
 * @param className - Additional className attributes to be applied to the card
 * @param maxHeight - The maximum height of the card (default is 500px). Increasing this will allow more content to be shown, but too high and the animation will look weird. Mostly used for the TaskOuterContainer component
 * @param hoverText - The text to be displayed when the card is clicked (default is "Actions")
 * @returns A card with the given children and a new tag if isNew is true
 */
export function CardBase({children, isNew, hoverChildren, canOpen=true, className, maxHeight=500, hoverText="Actions", hoverInsteadOfClick=false} : CardBaseProps){
    // Experimental
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const css_on_isNew = isNew ? 'border-2 border-green-200 hover:border-green-200' : 'dashboard-border'

    const onClick = (event: React.MouseEvent) => {
        event.stopPropagation()  // If CardBase is nested inside itself or has similar components, this will prevent the event from bubbling up and triggering the same function from its parent
        setIsOpen(!isOpen)
    }

    return (
        <div className={`${className} border-2 px-2 py-1 my-2 rounded-md shadow-md transition-all duration-300 dashboard-border 
            ${css_on_isNew} 
            ${!canOpen ? 'flex flex-row justify-between' : ''} 
            ${isOpen ? 'accent-dashboard-card' : 'dashboard-card'}
        `}
            onClick={onClick}
            onMouseEnter={hoverInsteadOfClick ? onClick : undefined}
            onMouseLeave={hoverInsteadOfClick ? onClick : undefined}
        >

            {isNew && <h4 className="text-green-500">New</h4>}
            {children}

            {canOpen && <div className={`overflow-hidden transition-all duration-200 ease-in-out ${
                isOpen 
                    ? `max-h-[${maxHeight}px] opacity-100 pointer-events-auto` 
                    : 'max-h-0 opacity-0 pointer-events-none'
            }`}>
                <div className={`transition-all duration-200 transform ${isOpen ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'}`}>
                    <p className="my-2 text-center text-gray-500 border-t-2 dark:text-stone-200">{hoverText}:</p>
                    {hoverChildren}
                </div>
            </div>}

            {/* This is for the contact us card or any card that doesn't want a hover function */}
            {!canOpen && hoverChildren}
            
            {canOpen && <div className={`cursor-pointer transition-all duration-200
                 ${isOpen ? 'rotate-180' : ''}
                 `}>
                <img src={down_arrow} alt="down_arrow" className="w-3 h-3 mx-auto dark:invert" />
            </div>}
        </div>
    )
}

/**
 * A card that displays a reminder text
 * 
 * Used in TaskCard.tsx, RFICard.tsx, and SubmittalCard.tsx
 * 
 * @param text - The text to be displayed in the card
 * @returns A card with the given text
 */
export function PingedCardAlert({text}: {text: string}) {
    return (
        <div className="flex flex-col p-4 rounded-md bg-red-100/50">
            <h4 className="text-red-600">{text}</h4>
        </div>
    )
}

// Color Coded Functions to help visually see the status of important fields
// (Although i don't think this is being used anymore)
export function ColorStatus(status : string) {
    if (status === "Active" || status === "Open" || status === "Paid")  {
        return "text-green-500"
    } else if (status === "Closing" || status === "Pending") {
        return "text-yellow-600"
    } else {
        return "text-red-500"
    }
}

export function DaysOldColor(days_old : number) {
    if (days_old < 10) {
        return "text-green-500"
    } else if (days_old < 20) {
        return "text-yellow-700"
    } else {
        return "text-red-500"
    }
}

export function HistoryCardBase({children}: {children: React.ReactNode}) {
    return (
        <div className="p-2 mx-2 my-4 transition-all duration-300 border-2 rounded-md shadow-md dashboard-card dashboard-border">
            {children}
        </div>
    )
}

export function formatDateTime(date: string) {
    const dateObj = new Date(date)
    return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric'
    })
}

export function formatDate(date: string) {
    const dateObj = new Date(date)
    return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })
}